/**
 * What a service is made of and how it runs, for the Apps tab's Overview, read from its folder: the file names of
 * `services/<name>` and the text of a few of them (FILES_TO_READ, at most MAX_READS), plus `.flow/service.json`. The
 * rules are the preview-runner's (container-live/preview-runner README): `run: "compose"` (a compose file IS the app),
 * `run: "docker"` (the Dockerfile is built and run), else the detected stack or `start`; without a service.json a
 * compose file with a `build:` service is the app. Nothing here is guessed beyond what those files say.
 */
import { COMPOSE_FILES, composeBuildsApp, composeFileIn, parseCompose } from './composeModel';
import { defaultEntry } from './shareEntry';

export const MAX_READS = 6;
/** The files whose text tells the technologies; the others count by name only. */
export const FILES_TO_READ = ['package.json', 'requirements.txt', 'pyproject.toml', 'pom.xml', 'Dockerfile', ...COMPOSE_FILES];

/** The ones of FILES_TO_READ the folder has, at most MAX_READS (one compose file is enough). */
export function filesToRead(names) {
  const compose = composeFileIn(names);
  return FILES_TO_READ.filter((file) => (names || []).includes(file) && (!COMPOSE_FILES.includes(file) || file === compose)).slice(0, MAX_READS);
}

/** service.json's text as an object, or null (missing, not JSON, not an object). */
export function parseSpec(text) {
  if (!text) { return null; }
  try {
    const spec = JSON.parse(text);
    return spec && typeof spec === 'object' && !Array.isArray(spec) ? spec : null;
  } catch {
    return null;
  }
}

const NODE_LIBS = [
  ['next', 'Next.js'], ['react', 'React'], ['vue', 'Vue'], ['@angular/core', 'Angular'], ['svelte', 'Svelte'], ['vite', 'Vite'],
  ['express', 'Express'], ['fastify', 'Fastify'], ['@nestjs/core', 'NestJS'], ['koa', 'Koa'], ['typescript', 'TypeScript'],
  ['tailwindcss', 'Tailwind CSS'], ['@prisma/client', 'Prisma'], ['pg', 'PostgreSQL'], ['mongodb', 'MongoDB'], ['mongoose', 'MongoDB'],
  ['redis', 'Redis'], ['ioredis', 'Redis'], ['kafkajs', 'Kafka'], ['better-sqlite3', 'SQLite'], ['sqlite3', 'SQLite'],
];
const PYTHON_LIBS = [
  ['fastapi', 'FastAPI'], ['flask', 'Flask'], ['django', 'Django'], ['streamlit', 'Streamlit'], ['sqlalchemy', 'SQLAlchemy'],
  ['psycopg2', 'PostgreSQL'], ['psycopg', 'PostgreSQL'], ['pymongo', 'MongoDB'], ['redis', 'Redis'],
];

function packageDeps(text) {
  const pkg = parseSpec(text);
  return pkg ? { ...(pkg.dependencies || {}), ...(pkg.devDependencies || {}) } : {};
}
// A Python package named in requirements.txt or pyproject.toml (a whole word, any version pin after it).
const mentionsPackage = (text, name) => new RegExp(`(^|[\\s"'\\[,])${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([\\s=<>~!\\[;"',]|$)`, 'im').test(text || '');

/** The technologies the folder shows, in a stable order, each once: `['Node.js', 'React', 'Vite', 'Docker']`. */
export function detectTechnologies({ names = [], files = {} } = {}) {
  const found = [];
  const add = (name) => { if (!found.includes(name)) { found.push(name); } };
  const has = (file) => names.includes(file);
  if (has('package.json')) {
    add('Node.js');
    const deps = packageDeps(files['package.json']);
    NODE_LIBS.forEach(([lib, label]) => { if (deps[lib] !== undefined) { add(label); } });
  }
  if (has('requirements.txt') || has('pyproject.toml')) {
    add('Python');
    const text = `${files['requirements.txt'] || ''}\n${files['pyproject.toml'] || ''}`;
    PYTHON_LIBS.forEach(([lib, label]) => { if (mentionsPackage(text, lib)) { add(label); } });
  }
  if (has('go.mod')) { add('Go'); }
  if (has('Cargo.toml')) { add('Rust'); }
  if (has('pom.xml')) {
    add('Java');
    add('Maven');
    if (/spring-boot/.test(files['pom.xml'] || '')) { add('Spring Boot'); }
  }
  if (has('build.gradle') || has('build.gradle.kts')) { add('Java'); add('Gradle'); }
  if (has('index.html') && !has('package.json')) { add('Static HTML'); }
  if (has('Dockerfile')) { add('Docker'); }
  if (composeFileIn(names)) { add('Docker Compose'); }
  return found;
}

const COMMAND_BUILDERS = [
  [/^(npm|npx)$/, 'Node / npm'], [/^yarn$/, 'Node / yarn'], [/^pnpm$/, 'Node / pnpm'], [/^(node|bun|deno)$/, 'Node'],
  [/^poetry$/, 'Python / Poetry'], [/^(python3?|pip3?|uvicorn|gunicorn|flask|uv)$/, 'Python / pip'], [/^go$/, 'Go'],
  [/^cargo$/, 'Rust / cargo'], [/^(mvn|\.\/mvnw)$/, 'Java / Maven'], [/^(gradle|\.\/gradlew)$/, 'Java / Gradle'], [/^make$/, 'Make'],
];
const commandOf = (value) => (Array.isArray(value) ? value.join(' ') : String(value || '')).trim();

/** Who builds a service run from its code: service.json's own commands first, then the runner's label, then the files. */
export function builderOf({ spec = null, names = [], stackLabel = null } = {}) {
  for (const field of ['install', 'build', 'start']) {
    const first = commandOf(spec?.[field]).split(/\s+/)[0];
    const match = first && COMMAND_BUILDERS.find(([pattern]) => pattern.test(first));
    if (match) { return match[1]; }
  }
  if (stackLabel) { return stackLabel; }
  const has = (file) => names.includes(file);
  if (has('package.json')) { return has('yarn.lock') ? 'Node / yarn' : has('pnpm-lock.yaml') ? 'Node / pnpm' : 'Node / npm'; }
  if (has('requirements.txt') || has('pyproject.toml')) { return 'Python / pip'; }
  if (has('go.mod')) { return 'Go'; }
  if (has('Cargo.toml')) { return 'Rust / cargo'; }
  if (has('pom.xml')) { return 'Java / Maven'; }
  if (has('build.gradle') || has('build.gradle.kts')) { return 'Java / Gradle'; }
  if (has('index.html')) { return 'Static files'; }
  return spec?.start ? 'Its own start command' : null;
}

const baseImage = (dockerfile) => String(dockerfile || '').match(/^\s*FROM\s+(?:--\S+\s+)*(\S+)/im)?.[1] || null;

/**
 * How the service runs: `{ kind: 'compose' | 'docker' | 'code', label, detail, file? }`. `detail` names the compose
 * file and entry, the Dockerfile and its base image, or the builder (null when no stack is detected and there is no
 * `start`).
 */
export function howItRuns({ spec = null, names = [], files = {}, stackLabel = null } = {}) {
  const composeFile = (spec?.run === 'compose' && (spec.file || spec.compose)) || composeFileIn(names);
  const composeServices = composeFile ? parseCompose(files[composeFile]) : [];
  if (spec?.run === 'compose' || (!spec && composeFile && composeBuildsApp(composeServices))) {
    return { kind: 'compose', label: 'Docker Compose', file: composeFile, detail: [composeFile, spec?.entry ? `entry ${spec.entry}` : null].filter(Boolean).join(' · ') || null };
  }
  if (spec?.run === 'docker') {
    const file = spec.dockerfile || 'Dockerfile';
    const image = baseImage(files[file]);
    return { kind: 'docker', label: 'Docker run', file, detail: image ? `${file} · from ${image}` : file };
  }
  const builder = builderOf({ spec, names, stackLabel });
  const deps = spec?.compose || (composeFile && spec?.run !== 'stack' ? composeFile : null);
  return { kind: 'code', label: 'Built from code', detail: [builder, deps ? `dependencies from ${deps}` : null].filter(Boolean).join(' · ') || null };
}

/** Whether the service's own folder or the runner says it is a Docker Compose app (the Apps list's "Compose" badge). */
export function isComposeService({ names = [], runMode = null, spec = null } = {}) {
  if (spec?.run === 'compose' || runMode === 'compose') { return true; }
  if (spec && spec.run && spec.run !== 'compose') { return false; }
  return Boolean(composeFileIn(names));
}

/**
 * Whether `name` (short) is the project's main service: the one a shared preview link opens by default, the project's
 * default app (`preferred`, utils/defaultApp) when set.
 */
export function isMainService(names, name, preferred = null) {
  const sorted = [...(names || [])].sort((a, b) => a.localeCompare(b));
  return Boolean(name) && defaultEntry(sorted, preferred) === name;
}
